import React, { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useCards, useCredits, useCreditPaymentSocket } from '../hooks';
import { CardFormFields, emptyCardForm } from './CardFormFields';
import type { CardFormData } from './CardFormFields';
import { PixQrCodeDisplay } from './PixQrCodeDisplay';
import type { CompanyCardResponse } from '../types';
import { CompanyEditDrawer } from '@/modules/settings/company/CompanyEditDrawer';

interface ReactivateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: {
    paymentMethod: 'credit_card' | 'pix';
    planName?: string;
    creditsPerCycle?: number;
  };
}

const numberFormat = new Intl.NumberFormat('pt-BR');

/**
 * Modal de reativação de assinatura — Modal shared (F72).
 */
export const ReactivateSubscriptionModal: React.FC<ReactivateSubscriptionModalProps> = ({
  isOpen,
  onClose,
  subscription,
}) => {
  const { cards, isLoading: isLoadingCards } = useCards(isOpen);
  const { reactivateSubscriptionAsync, isReactivating } = useCredits();
  const { confirmedPayment, resetConfirmedPayment } = useCreditPaymentSocket();

  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [newCard, setNewCard] = useState<CardFormData>({ ...emptyCardForm });
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);
  const [pixData, setPixData] = useState<{ qrCode: string; copyPaste: string; expiresAt: string } | null>(null);

  // Auto-select default card when opening with credit_card method
  useEffect(() => {
    if (isOpen && subscription.paymentMethod === 'credit_card' && cards && cards.length > 0 && selectedCardIndex === null) {
      const defaultIdx = cards.findIndex((c: CompanyCardResponse) => c.isDefault);
      setSelectedCardIndex(defaultIdx >= 0 ? defaultIdx : 0);
    }
  }, [isOpen, cards, selectedCardIndex, subscription.paymentMethod]);

  // Reset state when modal closes (inclui filho CompanyEditDrawer — ciclo pai→filho)
  useEffect(() => {
    if (!isOpen) {
      setSelectedCardIndex(null);
      setShowNewCard(false);
      setNewCard({ ...emptyCardForm });
      setPixData(null);
      setCompanyDrawerOpen(false);
      resetConfirmedPayment();
    }
  }, [isOpen, resetConfirmedPayment]);

  // Close modal when PIX payment is confirmed via socket
  useEffect(() => {
    if (confirmedPayment && pixData) {
      toast.success('Assinatura reativada com sucesso!');
      onClose();
    }
  }, [confirmedPayment, pixData, onClose]);

  const isCardMethod = subscription.paymentMethod === 'credit_card';

  const canConfirm = (): boolean => {
    if (!isCardMethod) return true; // PIX just needs confirm
    if (showNewCard) {
      return !!(
        newCard.holderName.trim() &&
        newCard.number.length === 16 &&
        newCard.expiryMonth.length === 2 &&
        newCard.expiryYear.length === 2 &&
        newCard.cvv.length === 3
      );
    }
    return selectedCardIndex !== null;
  };

  const handleConfirm = async () => {
    try {
      const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
      const result = await reactivateSubscriptionAsync({
        ...(isCardMethod && !showNewCard && selectedCardIndex !== null
          ? { cardIndex: selectedCardIndex }
          : {}),
        ...(isCardMethod && showNewCard
          ? {
              newCard: {
                number: newCard.number,
                cvv: newCard.cvv,
                expiryMonth: parseInt(newCard.expiryMonth, 10),
                expiryYear: currentCentury + parseInt(newCard.expiryYear, 10),
                holderName: newCard.holderName,
                nickname: newCard.nickname || undefined,
                setAsDefault: true,
              },
            }
          : {}),
      });

      if (result.paymentStatus === 'confirmed') {
        toast.success('Assinatura reativada com sucesso!');
        onClose();
      } else if (result.paymentStatus === 'pending' && result.pixData) {
        setPixData(result.pixData);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.message || error.response?.data?.error || '';
        if (typeof msg === 'string' && (msg.includes('CPF') || msg.includes('CNPJ'))) {
          setCompanyDrawerOpen(true);
        }
      }
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
        <div data-testid="reactivate-subscription-modal">
          <ModalHeader title="Reativar Assinatura" onClose={isReactivating ? undefined : onClose}>
            <span className="text-sm truncate" style={{ color: '#6b7280' }}>
              Retome seu plano atual
            </span>
          </ModalHeader>

          <ModalContent className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto" padding={false}>
            {/* PIX QR Code display (after successful reactivation with pending PIX) */}
            {pixData ? (
              <div className="space-y-4">
                <div
                  className="flex items-start gap-3 p-4 rounded-lg"
                  style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
                >
                  <Icon name="check-circle" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                  <p className="text-sm" style={{ color: '#15803d' }}>
                    Assinatura reativada! Pague o PIX abaixo para ativar seus créditos.
                  </p>
                </div>
                <PixQrCodeDisplay
                  qrCode={pixData.qrCode}
                  copyPaste={pixData.copyPaste}
                  expiresAt={pixData.expiresAt}
                />
              </div>
            ) : (
              <>
                {/* Plan info */}
                <div
                  className="flex items-start gap-3 p-4 rounded-lg"
                  style={{ backgroundColor: '#f3e8ff', border: '1px solid #e9d5ff' }}
                >
                  <Icon name="layers" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#8b5cf6' }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#6b21a8' }}>
                      {subscription.planName || 'Plano atual'}
                    </p>
                    {subscription.creditsPerCycle != null && (
                      <p className="text-sm" style={{ color: '#7c3aed' }}>
                        {numberFormat.format(subscription.creditsPerCycle)} créditos/ciclo
                      </p>
                    )}
                  </div>
                </div>

                {isCardMethod ? (
                  /* Credit card selection */
                  <div className="space-y-4">
                    <div
                      className="flex items-start gap-3 p-4 rounded-lg"
                      style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}
                    >
                      <Icon name="info" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#0284c7' }} />
                      <p className="text-sm" style={{ color: '#075985' }}>
                        O cartao selecionado sera cobrado imediatamente para reativar sua assinatura
                      </p>
                    </div>

                    {/* Card selection */}
                    <div className="space-y-3">
                      {isLoadingCards ? (
                        <div className="animate-pulse space-y-2">
                          {[1, 2].map((i) => (
                            <div
                              key={i}
                              className="h-14 rounded-lg"
                              style={{ backgroundColor: '#f3f4f6' }}
                            />
                          ))}
                        </div>
                      ) : (
                        <>
                          {/* Saved cards */}
                          {cards && cards.length > 0 && !showNewCard && (
                            <div className="space-y-2">
                              {cards.map((card: CompanyCardResponse, idx: number) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setSelectedCardIndex(idx)}
                                  className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors"
                                  style={{
                                    backgroundColor: selectedCardIndex === idx ? '#f3e8ff' : '#f9fafb',
                                    border: `2px solid ${selectedCardIndex === idx ? '#8b5cf6' : '#e5e7eb'}`,
                                  }}
                                  data-testid={`reactivate-card-${idx}`}
                                >
                                  <Icon name="credit-card" className="h-5 w-5 flex-shrink-0" style={{ color: '#6b7280' }} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                                      {card.brand} **** {card.maskedNumber.slice(-4)}
                                    </p>
                                    <p className="text-xs" style={{ color: '#9ca3af' }}>
                                      {card.holderName} - {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                                    </p>
                                  </div>
                                  {card.isDefault && (
                                    <span
                                      className="text-xs px-2 py-0.5 rounded-full"
                                      style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
                                    >
                                      Padrao
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Toggle add new */}
                          {!showNewCard ? (
                            <button
                              type="button"
                              onClick={() => {
                                setShowNewCard(true);
                                setSelectedCardIndex(null);
                              }}
                              className="w-full flex items-center gap-2 p-3 rounded-lg text-sm transition-colors"
                              style={{
                                border: '2px dashed #e5e7eb',
                                color: '#6b7280',
                              }}
                              data-testid="reactivate-add-new-card"
                            >
                              <Icon name="plus" className="h-4 w-4" />
                              Adicionar novo cartao
                            </button>
                          ) : (
                            /* New card form */
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                                  Novo cartao
                                </span>
                                {cards && cards.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowNewCard(false);
                                      setNewCard({ ...emptyCardForm });
                                      const defaultIdx = cards.findIndex((c: CompanyCardResponse) => c.isDefault);
                                      setSelectedCardIndex(defaultIdx >= 0 ? defaultIdx : 0);
                                    }}
                                    className="text-xs"
                                    style={{ color: '#8b5cf6' }}
                                  >
                                    Usar cartao salvo
                                  </button>
                                )}
                              </div>
                              <CardFormFields card={newCard} onChange={setNewCard} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* PIX info */
                  <div
                    className="flex items-start gap-3 p-4 rounded-lg"
                    style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
                  >
                    <Icon name="smartphone" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                    <p className="text-sm" style={{ color: '#15803d' }}>
                      Um QR Code PIX sera gerado para pagamento. Sua assinatura sera reativada apos a confirmacao.
                    </p>
                  </div>
                )}
              </>
            )}
          </ModalContent>

          {!pixData ? (
            <ModalFooter>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isReactivating}
                data-testid="reactivate-cancel"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={!canConfirm() || isReactivating}
                data-testid="reactivate-submit"
              >
                {isReactivating
                  ? 'Processando...'
                  : isCardMethod
                    ? 'Reativar Assinatura'
                    : 'Gerar PIX e Reativar'}
              </Button>
            </ModalFooter>
          ) : (
            <ModalFooter>
              <Button variant="secondary" onClick={onClose} data-testid="reactivate-close">
                Fechar
              </Button>
            </ModalFooter>
          )}
        </div>
      </Modal>

      <CompanyEditDrawer
        isOpen={companyDrawerOpen}
        onClose={() => setCompanyDrawerOpen(false)}
      />
    </>
  );
};

ReactivateSubscriptionModal.displayName = 'ReactivateSubscriptionModal';
